import { useColorModeValue } from '@/components/ui/color-mode'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { uit } from '@/theme'
import {
  Avatar,
  Box,
  Button,
  HStack,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react'
import { FiLogOut, FiUser } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

/**
 * User Menu Component
 * Modern avatar dropdown with user profile and actions
 * Uses centralized UI constants from theme/foundations.js
 */
export function UserMenu() {
  const navigate = useNavigate()
  const menuBg = useColorModeValue('white', 'gray.900')
  const headerBg = useColorModeValue('bg.accent', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const avatarBg = useColorModeValue('brand.500', 'brand.400')
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
      navigate(ROUTES.LOGIN)
    } catch (error) {
      // Even if logout fails, redirect to login
      navigate(ROUTES.LOGIN)
    }
  }

  if (!user) {
    return null
  }

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button
          variant="ghost"
          p={0}
          borderRadius="xl"
          _hover={{ bg: useColorModeValue('gray.100', 'gray.800') }}
          transition={uit.animations.transition.base}
        >
          <HStack spacing={ 3 } cursor="pointer" px={2} py={1}>
            <Avatar.Root size="md">
              <Avatar.Image src={ user.avatar } alt={ user.username || user.email } />
              <Avatar.Fallback bg={ avatarBg } color="white" fontWeight="600">
                { (user.username || user.email || '?')[0].toUpperCase() }
              </Avatar.Fallback>
            </Avatar.Root>
            <Box display={{ base: 'none', md: 'block' }} textAlign="left">
              <Text fontSize="sm" fontWeight="600">
                { user.username || user.email }
              </Text>
              <Text fontSize="xs" color="text.secondary">
                { user.email }
              </Text>
            </Box>
          </HStack>
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            bg={ menuBg }
            borderRadius="md"
            boxShadow="lg"
            minW={uit.menu.user.minWidth}
            p={0}
            border="1px"
            borderColor={ borderColor }
          >
            {/* User Info Header */}
            <Box
              px={uit.menu.user.headerPadding.x}
              py={uit.menu.user.headerPadding.y}
              borderBottom="1px"
              borderColor={ borderColor }
              bg={ headerBg }
              borderTopRadius="md"
            >
              <HStack spacing={ 3 }>
                <Avatar.Root size={uit.menu.user.avatarSize}>
                  <Avatar.Image src={ user.avatar } alt={ user.username || user.email } />
                  <Avatar.Fallback bg={ avatarBg } color="white" fontWeight="600" fontSize="xl">
                    { (user.username || user.email || '?')[0].toUpperCase() }
                  </Avatar.Fallback>
                </Avatar.Root>
                <Box flex={1}>
                  <Text fontSize="md" fontWeight="700">
                    { user.username || user.email }
                  </Text>
                  <Text fontSize="sm" color="text.secondary" noOfLines={1}>
                    { user.email }
                  </Text>
                </Box>
              </HStack>
            </Box>

            {/* Menu Items */}
            <Box py={2}>
              <Menu.Item
                value="profile"
                onClick={ () => navigate(ROUTES.PROFILE) }
                px={uit.menu.user.itemPadding.x}
                py={uit.menu.user.itemPadding.y}
                borderRadius="sm"
                mx={2}
                transition={uit.animations.transition.base}
                _hover={{ bg: useColorModeValue('gray.100', 'gray.800') }}
              >
                <HStack spacing={ 3 }>
                  <Box as={ FiUser } boxSize={uit.menu.user.iconSize} color="gray.500" />
                  <Text fontWeight="500">Profile</Text>
                </HStack>
              </Menu.Item>
              <Box h="1px" bg={ borderColor } my={2} mx={2} />
              <Menu.Item
                value="logout"
                onClick={ handleLogout }
                px={uit.menu.user.itemPadding.x}
                py={uit.menu.user.itemPadding.y}
                borderRadius="sm"
                mx={2}
                color="error.500"
                transition={uit.animations.transition.base}
                _hover={{ bg: useColorModeValue('error.50', 'error.900') }}
              >
                <HStack spacing={ 3 }>
                  <Box as={ FiLogOut } boxSize={uit.menu.user.iconSize} />
                  <Text fontWeight="500">Logout</Text>
                </HStack>
              </Menu.Item>
            </Box>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export default UserMenu
